import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Spoken narration playback for a running tour.
 *
 * One reused Audio element; the manifest maps stepId → hashed mp3 under
 * /tour-audio/<slug>/. Browser autoplay policy is the design constraint: audio
 * may only start after a user gesture, and the tour's own Next/Start clicks are
 * exactly that — so `onStepChange` attempts autoplay and, when the browser
 * refuses (cold deep-link into step 1), we surface a play button instead of
 * erroring. Voice preference persists in localStorage (`fusegrid_tour_voice`,
 * '0' = muted); a 404'd or absent manifest means "this tour has no narration"
 * and every hook consumer renders nothing.
 */

const LS_VOICE_KEY = 'fusegrid_tour_voice'

export interface NarrationManifest {
  slug: string
  voiceId: string
  steps: Record<string, { file: string; hash: string; chars: number }>
}

export type NarrationState = 'idle' | 'playing' | 'paused' | 'blocked'

export function voicePreferred(): boolean {
  try {
    return window.localStorage.getItem(LS_VOICE_KEY) !== '0'
  } catch {
    return true
  }
}

export function setVoicePreferred(on: boolean): void {
  try {
    if (on) window.localStorage.removeItem(LS_VOICE_KEY)
    else window.localStorage.setItem(LS_VOICE_KEY, '0')
  } catch {
    /* private mode — session-only preference */
  }
}

export function useTourNarration(slug: string | null, playbackAllowed = true) {
  const [manifest, setManifest] = useState<NarrationManifest | null>(null)
  const [state, setState] = useState<NarrationState>('idle')
  const [enabled, setEnabled] = useState<boolean>(() => voicePreferred())
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const stepRef = useRef<string | null>(null)

  useEffect(() => {
    if (!slug || !playbackAllowed) { setManifest(null); return }
    const ctrl = new AbortController()
    fetch(`/tour-audio/${encodeURIComponent(slug)}/manifest.json`, { signal: ctrl.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((m) => { if (!ctrl.signal.aborted) setManifest(m && m.steps ? (m as NarrationManifest) : null) })
      .catch(() => { /* no narration for this tour — the control simply doesn't render */ })
    return () => ctrl.abort()
  }, [slug, playbackAllowed])

  const stop = useCallback(() => {
    const a = audioRef.current
    if (a) { a.pause(); a.removeAttribute('src'); a.load() }
    setState('idle')
  }, [])

  /** Step changed: stop the old step's audio; autoplay the new one if enabled. */
  const onStepChange = useCallback((stepId: string | null) => {
    stepRef.current = stepId
    const entry = stepId && manifest ? manifest.steps[stepId] : null
    stop()
    if (!playbackAllowed || !entry || !manifest || !enabled) return
    const a = audioRef.current ?? (audioRef.current = new Audio())
    a.src = `/tour-audio/${encodeURIComponent(manifest.slug)}/${entry.file}`
    a.onended = () => setState('idle')
    a.play().then(
      () => setState('playing'),
      () => setState('blocked'), // pre-gesture deep link — show ▶ instead
    )
  }, [manifest, enabled, playbackAllowed, stop])

  /** The card's single narration button: play/pause/replay for this step. */
  const toggle = useCallback(() => {
    if (!playbackAllowed) return
    const stepId = stepRef.current
    const entry = stepId && manifest ? manifest.steps[stepId] : null
    if (!entry || !manifest) return
    const a = audioRef.current ?? (audioRef.current = new Audio())
    if (state === 'playing') { a.pause(); setState('paused'); return }
    if (state === 'paused' && a.src) { void a.play().then(() => setState('playing'), () => setState('blocked')); return }
    a.src = `/tour-audio/${encodeURIComponent(manifest.slug)}/${entry.file}`
    a.onended = () => setState('idle')
    void a.play().then(() => { setState('playing'); if (!enabled) { setEnabled(true); setVoicePreferred(true) } }, () => setState('blocked'))
  }, [manifest, state, enabled, playbackAllowed])

  /**
   * The single control the visitor actually reasons about: narration on or off.
   * Turning it off stops whatever is speaking now; turning it on speaks the
   * current step immediately, so the button's effect is audible either way
   * rather than deferred to the next step.
   */
  const setNarrationEnabled = useCallback((on: boolean) => {
    if (!playbackAllowed) { stop(); return }
    setEnabled(on)
    setVoicePreferred(on)
    if (!on) { stop(); return }
    const stepId = stepRef.current
    const entry = stepId && manifest ? manifest.steps[stepId] : null
    if (!entry || !manifest) return
    const a = audioRef.current ?? (audioRef.current = new Audio())
    a.src = `/tour-audio/${encodeURIComponent(manifest.slug)}/${entry.file}`
    a.onended = () => setState('idle')
    void a.play().then(() => setState('playing'), () => setState('blocked'))
  }, [manifest, playbackAllowed, stop])

  const mute = useCallback(() => { setNarrationEnabled(false) }, [setNarrationEnabled])

  useEffect(() => () => { stop() }, [stop])

  useEffect(() => {
    if (!playbackAllowed) stop()
  }, [playbackAllowed, stop])

  const hasFor = useCallback(
    (stepId: string | null) => Boolean(playbackAllowed && stepId && manifest?.steps[stepId]),
    [manifest, playbackAllowed],
  )

  return {
    manifest: playbackAllowed ? manifest : null,
    state: playbackAllowed ? state : 'idle' as NarrationState,
    enabled: playbackAllowed && enabled,
    hasFor,
    onStepChange,
    toggle,
    mute,
    setNarrationEnabled,
  }
}
